/**
 * @file host.ts
 *
 * One registration for everything a host environment provides. A host is the
 * thing the app runs inside — a plain browser tab, or something like GrabCAD
 * Print's embedded webview — and a host plugin (e.g. grabcadPlugin.ts) tells
 * the library how that host differs, keyed by the env string as-app resolves:
 *
 *   registerHost('grabcad', {
 *     window:     { quit, vetoesUnload: true },
 *     fileSave:   { saveFile, saveStatusTrackable: true },
 *     fileImport: { kinds: ['mesh'], pickFile, claimDrop },
 *     prefs:      { loadAll, saveAll },
 *     settings:   { allowTransferNewTab: false },
 *   });
 *
 * Each section is optional and has its own consumer module, which is where its
 * interface is documented and where the library reads it: fileSave.ts,
 * features/importExport/import/fileImportProvider.ts, prefsBackend.ts, data/appSettings.ts,
 * and the window section below. Core code never imports host-specific logic;
 * it asks getHost() for the current env's sections and falls back to plain
 * browser behaviour when a section is missing.
 *
 * Which env is active is a separate question, answered by data/envRegistry.ts
 * from the sign-in answer. `checkHost` recognises a host when there is no
 * sign-in answer, such as after the session expired. See resolveSessionEnv().
 */

import { internalData } from '../../store/store.ts';
import { sendEv } from '../../data/events.ts';
import type { FileSaveProvider } from './fileSave.ts';
import type { FileImportProvider } from '../importExport/import/fileImportProvider.ts';
import type { PrefsBackend } from './prefsBackend.ts';
import type { AppSettings } from '../../data/appSettings.ts';
import type { UserData } from '../serverApi/asApi.ts';
import { DEFAULT_ENV, resolveEnvFromUserData } from '../../data/envRegistry.ts';

// ============================================================================
// Window section
// ============================================================================

/** Window event a host plugin dispatches when the host's close button was
 *  pressed. Cancelable: preventDefault keeps the window open. as-app only ever
 *  listens for this internal name; see requestClose(). */
export const CLOSE_REQUESTED_EVENT = 'as-close-requested';

/**
 * How the host's window closes. An Electron-style host differs from a browser
 * tab in three ways as-app's exit guards care about: window.close() may be a
 * no-op in its webview, it may ask before closing, and it may honour a
 * beforeunload veto quietly. Without this section as-app behaves like a
 * browser tab: window.close() and no beforeunload veto.
 */
export interface WindowHost {
  /**
   * Close the window the host's way. `close` is the plain `window.close()`,
   * for hosts whose quit callback wants to be handed it (`onQuit(close)`).
   * Leave out to just call `window.close()`.
   */
  quit?(close: () => void): void;
  /**
   * True when the host cancels the unload on a truthy `beforeunload`
   * returnValue without showing a dialog of its own, so as-app can veto an
   * accidental close while busy or unsaved. Leave out for browser-like hosts,
   * where the veto would pop the browser's generic "leave site?" prompt on top
   * of our modal.
   */
  vetoesUnload?: true;
}

/**
 * Dispatches `as-close-requested` for a host's own close event and reports
 * whether as-app asked to stay. A plugin's bridge is one line with it:
 *   window.addEventListener('closeRequested', e => { if (requestClose()) e.preventDefault(); });
 */
export function requestClose(): boolean {
  // sendEv returns dispatchEvent's boolean: false once a listener has vetoed.
  return !sendEv(window, CLOSE_REQUESTED_EVENT, { cancelable: true });
}

// ============================================================================
// Registry
// ============================================================================

/**
 * Asks the host whether it is this one, for a load with no sign-in answer.
 * Resolves false when it cannot tell. A signed-out load waits for it, so it
 * must settle quickly.
 */
export type HostCheck = () => Promise<boolean>;

export interface HostProvider {
  /** Recognises the host with no sign-in answer. Read by resolveSessionEnv(). */
  checkHost?: HostCheck;
  /** Window close behaviour. Read by as-app's ExitMixin. */
  window?: WindowHost;
  /** Native save dialog. Read by saveFileAs() in fileSave.ts. */
  fileSave?: FileSaveProvider;
  /** Native open dialog and file conversion. Read by <as-file-input>. */
  fileImport?: FileImportProvider;
  /** A store for prefs that survives the host wiping browser storage. Read by db.ts. */
  prefs?: PrefsBackend;
  /** Behavioural toggles. Merged over the defaults by getAppSettings(). */
  settings?: Partial<AppSettings>;
}

const hosts = new Map<string, HostProvider>();

/**
 * Register (or extend) what a host provides for an env. Called by plugin
 * modules on load. Repeated calls for the same env replace each section given
 * and leave the others alone; `settings` merge shallowly, last value wins.
 */
export function registerHost(env: string, provider: HostProvider): void {
  const existing = hosts.get(env) ?? {};
  hosts.set(env, {
    ...existing,
    ...provider,
    settings: provider.settings ? { ...existing.settings, ...provider.settings } : existing.settings,
  });
}

/** What the given env's host provides (defaults to the current appEnv), or
 *  undefined when nothing is registered for it, i.e. a plain browser tab. */
export function getHost(env: string | null | undefined = internalData.appEnv): HostProvider | undefined {
  return env ? hosts.get(env) : undefined;
}

// ============================================================================
// Session env
// ============================================================================

/**
 * The env for this session. With userData, the host that matches it, or the
 * default. Without userData (signed out, or the session expired), the first
 * host whose checkHost() confirms it, or the default.
 */
export async function resolveSessionEnv(userData: UserData | null | undefined): Promise<string> {
  if (userData) return resolveEnvFromUserData(userData) ?? DEFAULT_ENV;

  for (const [env, host] of hosts) {
    if (await host.checkHost?.()) return env;
  }
  return DEFAULT_ENV;
}
